import {
  Injectable,
  InjectionToken,
  NgZone,
  signal,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular/standalone';
import { API_CLIENT } from '../http/api-client-token';
import { AuthStore } from '../auth/auth.store';
import { normalizePushRoute } from '../deep-link.service';
import { environment } from '../../../environments/environment';
import type { PushPayload, PushState, PushPlatform } from './push.types';

/** Storage key for "we already asked you once — don't nag every login". */
export const PUSH_PROMPT_SHOWN_KEY = 'lance.push.promptShown';

/**
 * Phase 15 (PUSH-01/PUSH-03) — cross-platform push notifications service.
 *
 * Why DI ports instead of direct `import`:
 *   The `@capacitor/push-notifications` and `firebase/messaging` SDKs are
 *   side-effecting and not directly mockable in the Angular DI graph. We wrap
 *   them in two thin ports (`PUSH_NATIVE_PORT` + `PUSH_WEB_PORT`) so:
 *     - production wiring uses dynamic import in the providers (lazy + tree-
 *       shake-friendly when push isn't used);
 *     - spec wiring injects pure JS fakes (mirrors the CAMERA_PORT pattern from
 *       Phase 11 image-upload.component.spec.ts).
 *
 * Threats covered:
 *   T-15-08: every external listener wraps `this.zone.run` (mirrors
 *            DeepLinkService) so Angular change detection never gets stuck.
 *   T-15-09: revokeCurrentToken() is awaited BEFORE clearing JWT in
 *            AuthStore.logout (wiring lives in Task 2).
 *   T-15-10: deep-link payloads pass through isSafeDeepLink() — must be a path
 *            starting with `/` and never contain `://`.
 *   T-15-11: prompt UX only after FIRST login (auth.store.ts hook),
 *            never on signup; localStorage guard in promptForPermission*.
 */

// ---------------------------------------------------------------------------
// Ports — replaced by jest fakes in spec, wired to real SDKs by the provider.
// ---------------------------------------------------------------------------

export interface PushNativePort {
  isNative: boolean;
  getPlatform: () => string;
  requestPermissions: () => Promise<{ receive: 'granted' | 'denied' | 'prompt' }>;
  register: () => Promise<void>;
  addListener: (
    eventName:
      | 'registration'
      | 'registrationError'
      | 'pushNotificationReceived'
      | 'pushNotificationActionPerformed',
    cb: (payload: unknown) => void,
  ) => Promise<unknown>;
  removeAllListeners: () => Promise<void>;
}

export interface PushWebPort {
  requestPermission: () => Promise<'granted' | 'denied' | 'default'>;
  registerServiceWorker: (path: string) => Promise<ServiceWorkerRegistration>;
  getFcmToken: (params: {
    firebaseWeb: typeof environment.firebaseWeb;
    serviceWorkerRegistration: ServiceWorkerRegistration;
  }) => Promise<string | null>;
  onMessage: (handler: (payload: PushPayload) => void) => () => void;
}

export interface PushBrowserEnv {
  hasNotificationApi: boolean;
  hasServiceWorker: boolean;
  firebaseWeb: typeof environment.firebaseWeb;
}

export const PUSH_NATIVE_PORT = new InjectionToken<PushNativePort>('PUSH_NATIVE_PORT');
export const PUSH_WEB_PORT = new InjectionToken<PushWebPort>('PUSH_WEB_PORT');
export const PUSH_BROWSER_ENV = new InjectionToken<PushBrowserEnv>('PUSH_BROWSER_ENV');

// ---------------------------------------------------------------------------
// Deep-link safety guard (Threat T-15-10).
// ---------------------------------------------------------------------------

export function isSafeDeepLink(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('://')
  );
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly api = inject(API_CLIENT);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly native = inject(PUSH_NATIVE_PORT);
  private readonly web = inject(PUSH_WEB_PORT);
  private readonly env = inject(PUSH_BROWSER_ENV);
  private readonly alertCtrl = inject(AlertController);
  private readonly authStore = inject(AuthStore);

  private readonly _state = signal<PushState>({
    permission: 'prompt',
    token: null,
    platform: null,
  });
  readonly state = this._state.asReadonly();

  /**
   * Called by AuthStore/AuthService after a successful login (NOT signup).
   * Idempotent: re-calling after a successful registration is a no-op against
   * the backend because the token row upserts on conflict (push.controller.ts).
   */
  async registerAfterLogin(): Promise<void> {
    if (this.native.isNative) {
      await this.registerNative();
      return;
    }
    await this.registerWeb();
  }

  private async registerNative(): Promise<void> {
    const perm = await this.native.requestPermissions();
    if (perm.receive !== 'granted') {
      this._state.update((s) => ({ ...s, permission: 'denied' }));
      return;
    }
    const platform = this.native.getPlatform() as PushPlatform;

    // Listener must be attached BEFORE register() or we drop the registration
    // event on the floor.
    await this.native.addListener('registration', (payload) => {
      const token = (payload as { value?: string } | undefined)?.value;
      if (!token) return;
      this.zone.run(() => {
        this._state.set({ permission: 'granted', token, platform });
        void this.api.push
          .registerToken({ token, platform })
          .catch((err) => console.warn('[push] registerToken failed', err));
      });
    });

    await this.native.addListener('registrationError', (err) => {
      this.zone.run(() => {
        console.warn('[push] registrationError', err);
        this._state.update((s) => ({ ...s, permission: 'denied' }));
      });
    });

    await this.native.register();
    this._state.update((s) => ({ ...s, permission: 'granted', platform }));
  }

  private async registerWeb(): Promise<void> {
    // Bail fast when the browser cannot do push or Firebase config is unset.
    if (
      !this.env.hasNotificationApi ||
      !this.env.hasServiceWorker ||
      this.env.firebaseWeb.apiKey === 'REPLACE_AT_DEPLOY'
    ) {
      this._state.update((s) => ({ ...s, permission: 'unsupported' }));
      return;
    }

    const perm = await this.web.requestPermission();
    if (perm !== 'granted') {
      this._state.update((s) => ({ ...s, permission: 'denied' }));
      return;
    }

    let serviceWorkerRegistration: ServiceWorkerRegistration;
    try {
      serviceWorkerRegistration = await this.web.registerServiceWorker(
        '/firebase-messaging-sw.js',
      );
    } catch (err) {
      console.warn('[push] SW registration failed', err);
      this._state.update((s) => ({ ...s, permission: 'unsupported' }));
      return;
    }

    let token: string | null = null;
    try {
      token = await this.web.getFcmToken({
        firebaseWeb: this.env.firebaseWeb,
        serviceWorkerRegistration,
      });
    } catch (err) {
      console.warn('[push] getFcmToken failed', err);
    }

    if (!token) {
      this._state.update((s) => ({ ...s, permission: 'denied' }));
      return;
    }

    this._state.set({ permission: 'granted', token, platform: 'web' });
    try {
      await this.api.push.registerToken({ token, platform: 'web' });
    } catch (err) {
      console.warn('[push] registerToken failed', err);
    }
  }

  /**
   * Revoke the server-side token row + tear down native listeners.
   *
   * Best-effort: any HTTP error is swallowed because we MUST clear local state
   * even if the server is down (otherwise logout would block on push UX).
   */
  async revokeCurrentToken(): Promise<void> {
    const current = this._state();
    if (current.token) {
      try {
        await this.api.push.revokeToken(current.token);
      } catch (err) {
        console.warn('[push] revokeToken failed', err);
      }
    }
    if (this.native.isNative) {
      try {
        await this.native.removeAllListeners();
      } catch {
        // ignore
      }
    }
    this._state.set({ permission: 'prompt', token: null, platform: null });
  }

  /**
   * Attach the tap-handler that routes through Angular Router.
   *
   * Native: PushNotifications.pushNotificationActionPerformed event.
   * Web: the SW posts a `lance:push-deeplink` CustomEvent on `window` from
   *      notificationclick — handled by DeepLinkService (Task 2 bridge).
   */
  attachDeepLinkListeners(): void {
    if (!this.native.isNative) return;
    void this.native.addListener('pushNotificationActionPerformed', (payload) => {
      const data = (payload as { notification?: { data?: Record<string, unknown> } } | undefined)
        ?.notification?.data;
      const candidate = data?.['deepLink'];
      if (!isSafeDeepLink(candidate)) return;
      const normalized = normalizePushRoute(candidate, this.authStore.user()?.role);
      this.zone.run(() => {
        void this.router.navigateByUrl(normalized);
      });
    });
  }

  /**
   * Phase 15 (PUSH-03 / D-PUSH-03) — permission prompt UX.
   *
   * Called from AuthStore/AuthService AFTER a successful LOGIN (NOT signup —
   * T-15-11). Idempotent: `lance.push.promptShown` flag in localStorage gates
   * re-prompts across sessions. The user can always re-enable through the
   * /configuracoes/notificacoes Settings page (Task 3).
   *
   * Copy locked in 15-CONTEXT.md D-PUSH-03.
   */
  async promptForPermissionAfterFirstLogin(): Promise<void> {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(PUSH_PROMPT_SHOWN_KEY) === '1') {
        return;
      }
    } catch {
      // Storage unavailable — fall through and prompt this once.
    }

    const alert = await this.alertCtrl.create({
      header: 'Receber avisos',
      message:
        'Para receber avisos quando sua candidatura for aprovada, ative notificações.',
      buttons: [
        { text: 'Agora não', role: 'cancel' },
        {
          text: 'Ativar',
          handler: () => {
            // Don't block alert dismissal on the network round-trip.
            void this.registerAfterLogin().catch((err) =>
              console.warn('[push] registerAfterLogin failed', err),
            );
          },
        },
      ],
    });
    await alert.present();
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(PUSH_PROMPT_SHOWN_KEY, '1');
      }
    } catch {
      // ignore
    }
  }

  /**
   * Wired by Task 2: this is the hook the FOREGROUND banner attaches to.
   * Exposed as a method so the banner service injection happens in Task 2
   * without creating a hard dependency from Task 1's push.service file.
   */
  attachForegroundHandler(handler: (payload: PushPayload) => void): void {
    if (this.native.isNative) {
      void this.native.addListener('pushNotificationReceived', (payload) => {
        const n = payload as
          | { title?: string; body?: string; data?: Record<string, unknown> }
          | undefined;
        this.zone.run(() => {
          handler({ title: n?.title ?? '', body: n?.body ?? '', data: n?.data });
        });
      });
    } else {
      this.web.onMessage((p) => this.zone.run(() => handler(p)));
    }
  }
}
